/**
 * Current-user cache storage.
 *
 * Goals:
 * - Deduplicate concurrent getCurrentUser calls (module-level promise cache).
 * - Persist decoded user data in sessionStorage so reloads don't refetch.
 * - Keep dependency-free (no API client imports) so Auth/logout can import safely.
 */

const CACHE_KEY = "user_current_user_data_cache_v1";

let currentUserPromiseCache: Promise<any> | null = null;

const isBrowser = (): boolean => typeof window !== "undefined";

const safeSessionGetItem = (key: string): string | null => {
  if (!isBrowser()) return null;
  try {
    return localStorage.getItem(key);
  } catch (err) {
    console.warn("Failed to access local storage:", err);
    return null;
  }
};

const safeSessionSetItem = (key: string, value: string): void => {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    console.warn("Failed to set local storage:", err);
  }
};

const safeSessionRemoveItem = (key: string): void => {
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.warn("Failed to remove from local storage:", err);
  }
};

export function getCurrentUserCacheKey(): string {
  return CACHE_KEY;
}

export function getCurrentUserPromiseCache(): Promise<any> | null {
  return currentUserPromiseCache;
}

export function setCurrentUserPromiseCache(p: Promise<any> | null): void {
  currentUserPromiseCache = p;
}

export function clearCurrentUserCache(): void {
  safeSessionRemoveItem(CACHE_KEY);
  currentUserPromiseCache = null;
}

export function readCurrentUserDataFromSession(): any | null {
  const raw = safeSessionGetItem(CACHE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { data?: any };
    return parsed?.data ?? null;
  } catch {
    return null;
  }
}

export function writeCurrentUserDataToSession(data: any): void {
  safeSessionSetItem(CACHE_KEY, JSON.stringify({ data, cachedAt: Date.now() }));
}

